import { observable, makeObservable, runInAction, action } from 'mobx';
import { get, post, patch, deleteById } from 'stores/_services/dataService';

export interface ITableView {
    id?: string;
    tableName: string;
    viewName: string;
    columns: { visibleColumns: string[]; columnOrder: string[] };
    userId?: string;
}

export default class TableViewStore {
    items: ITableView[] = [];
    isTableOptionsOpen = false;

    constructor() {
        makeObservable(this, {
            items: observable,
            isTableOptionsOpen: observable,
            toggleTableOptions: action,
            saveView: action,
            getTableView: action
        });
    }

    public async getTableView(tableName: string, userId: string): Promise<ITableView | null> {
        // Check localStorage first
        const localData = localStorage.getItem(`table-options-${tableName}-${userId}`);
        if (localData) {
            return JSON.parse(localData);
        }

        // Fetch from the database if not in localStorage
        try {
            const result = await get<ITableView[]>('/tableview', { filter: { where: { tableName, userId } } });

            if (result?.data?.length) {
                const tableView = result.data[0];
                localStorage.setItem(`table-options-${tableName}-${userId}`, JSON.stringify(tableView));
                return tableView;
            }
        } catch (e) {
            console.error(`Failed to fetch table view for ${tableName}:`, e);
        }

        return null;
    }

    public async saveView(tableName: string, columns: { visibleColumns: string[]; columnOrder: string[] }, userId: string): Promise<void> {
        const existingView = this.items.find((view) => view.tableName === tableName && userId === view.userId);
        const tableView: ITableView = {
            id: existingView?.id,
            tableName,
            viewName: 'User Default',
            columns
        };

        try {
            if (existingView) {
                await patch(`/tableview/${existingView.id}`, tableView);
            } else {
                const result = await post('/tableview', tableView);
                runInAction(() => {
                    this.items.push(result.data);
                });

                tableView.id = result.data.id;
            }
        } catch (e) {
            // console.error(`Failed to save table view for ${tableName}:`, e);
        } finally {
            // Save to localStorage regardless of errors
            localStorage.setItem(`table-options-${tableName}-${userId}`, JSON.stringify(tableView));
        }
    }

    public toggleTableOptions(value?: boolean) {
        this.isTableOptionsOpen = value !== undefined ? value : !this.isTableOptionsOpen;
    }
}
